import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, store } from "@redux";
import { categoriesApi } from "@api";
import { ITourElement, IOptionSearch } from "@interfaces";
import { sliceString, filterProducts } from "@utilities";
import { RootState } from ".";
import _ from "lodash";
import { PAGINATION } from "@constants";
import { IFTSearchParams } from "../../Api/categoriesApi";

interface ISearchTours {
    loading: boolean;
    listTours: ITourElement[];
    totalRecord: number;
    currentPage: number;
    sortBy: string;
    sortDirection: string;
}

const initialState: ISearchTours = {
    loading: false,
    listTours: [],
    totalRecord: 0,
    currentPage: 1,
    sortBy: "name",
    sortDirection: "asc",
};

type SearchCategoriesState = {
    [key: string]: any;
    activities?: Array<{ id: number | string }>;
    suppliers?: { id: number | string };
    transports?: { id: number | string };
    name?: string;
    day?: string | number;
    sortBy?: string;
    sortDirection?: string;
};

export const searchQuery =
    (loadMore: boolean, option: IOptionSearch, isDestinationExplore = false) =>
    async (dispatch: AppDispatch) => {
        dispatch(loading(true));
        const searchObject: SearchCategoriesState = store.getState().searchCategories;

        const params: IFTSearchParams = {
            page: store.getState().searchTours.currentPage,
            per_page: PAGINATION,
        };

        const getLocationText = (loc: Record<string, any> | null | undefined): string | undefined => {
            if (!loc || !_.isObject(loc)) return undefined;
            if ((loc as any).text) return (loc as any).text;
            if ((loc as any).id && typeof (loc as any).id === "string" && isNaN(Number((loc as any).id))) return (loc as any).id;
            return undefined;
        };

        if (isDestinationExplore) {
            const regionText = getLocationText(searchObject.regions);
            if (regionText) params.destination = regionText;
            const countryText = getLocationText(searchObject.countries);
            if (countryText) params.country = countryText;
        } else {
            if (searchObject.name) {
                params.q = searchObject.name;
            }
            if (_.isArray(searchObject.activities) && !_.isEmpty(searchObject.activities)) {
                params.activity = _.map(searchObject.activities, "id").join(",");
            }
            if (_.isArray(searchObject.accommodation) && !_.isEmpty(searchObject.accommodation)) {
                params.accommodation = _.map(searchObject.accommodation, "id").join(",");
            }
            if (_.isArray(searchObject.transports) && !_.isEmpty(searchObject.transports)) {
                params.transport = _.map(searchObject.transports, "id").join(",");
            }
            if (_.isArray(searchObject.suppliers) && !_.isEmpty(searchObject.suppliers)) {
                params.supplier = _.map(searchObject.suppliers, "id").join(",");
            }
            const countryText = getLocationText(searchObject.countries);
            if (countryText) params.country = countryText;

            if (_.isArray(searchObject.destinations) && !_.isEmpty(searchObject.destinations)) {
                params.destination = _.map(searchObject.destinations, "id").join(",");
            } else {
                const regionText = getLocationText(searchObject.regions);
                if (regionText) params.destination = regionText;
            }

            if (_.isArray(searchObject.startLocations) && !_.isEmpty(searchObject.startLocations)) {
                params.start_location = _.map(searchObject.startLocations, "id").join(",");
            } else {
                const localityText = getLocationText(searchObject.localities);
                if (localityText) params.start_location = localityText;
            }

            if (searchObject.duration && _.isObject(searchObject.duration) && _.has(searchObject.duration, "id")) {
                params.duration = String((searchObject.duration as any).id);
            } else if (searchObject.day && searchObject.day !== 0) {
                params.duration = String(searchObject.day);
            }
        }

        if (option?.sortBy) {
            params.sort_by = option.sortBy;
        }
        if (option?.sortDirection) {
            params.sort_direction = option.sortDirection;
        }

        try {
            const res = await categoriesApi.searchProducts(params);
            const responseData = res.data?.data ?? res.data;
            const products = filterProducts(responseData.products || []);
            const processData = products.map((tour: any) => {
                let duringTime = "";
                if (Number(tour.durationDays) > 0) {
                    duringTime += `${tour.durationDays} ${
                        Number(tour.durationDays) === 1 ? "day" : "days"
                    }`;
                }
                if (Number(tour.durationDays) > 0 && Number(tour.durationNight) > 0) {
                    duringTime += " - ";
                }
                if (Number(tour.durationNight) > 0) {
                    duringTime += `${tour.durationNight} ${
                        Number(tour.durationNight) === 1 ? "night" : "nights"
                    }`;
                }
                const location = tour.categories
                    ? `${tour.categories.startLocationName || ""}, ${(tour.categories.countryNames || [])[0] || ""}`
                    : tour.location || "";
                return {
                    productId: tour.productId,
                    name: tour.name,
                    location,
                    description: sliceString(tour.productTeaser ?? tour.description ?? "", 20),
                    duration: duringTime,
                    rrp: Number(tour.rrp).toFixed(2),
                    supplier: tour.supplierName ?? tour.supplier ?? "",
                    productImagePath: tour.productLargeSizeImagePath || tour.productImagePath,
                    faresprices: tour.faresprices,
                    categories: tour.categories || { startLocationName: "", countryNames: [] },
                } as unknown as ITourElement;
            });
            if (loadMore) {
                dispatch(pushTours(processData));
            } else {
                dispatch(setTours(processData));
            }
            dispatch(setTotalRecord(responseData.pagination?.total ?? 0));
            dispatch(loading(false));
        } catch (err: any) {
            console.error(err);
            dispatch(loading(false));
            return [];
        }
    };

const searchTours = createSlice({
    name: "searchTours",
    initialState,
    reducers: {
        loading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setTours: (state, action: PayloadAction<ITourElement[]>) => {
            state.listTours = action.payload;
        },
        setTotalRecord: (state, action: PayloadAction<number>) => {
            state.totalRecord = action.payload;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        pushTours: (state, action: PayloadAction<ITourElement[]>) => {
            state.listTours = [...state.listTours, ...action.payload];
        },
        clearListTour: (state) => {
            state.listTours = [];
        },
    },
});

export const selectSearchTour = (state: RootState) => state.searchTours;

export const { loading, setTours, setTotalRecord, setCurrentPage, pushTours, clearListTour } =
    searchTours.actions;
export default searchTours.reducer;
